import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ValidatedInput as Input } from '@glu/form-components';
import { pick } from 'lodash';
import LocaleContext from '../../../locale';
import useStyles from './Header.styles';
import MapNameInput from './MapName';
import DelimitedDropdown from './DelimitedDropdown';
import Checkbox from '../../../checkbox/Checkbox';
import { delimitedDropdownValidation, asyncExecuteMultiplyGluFunctions } from './utils';
import {
  FIXED_FORMAT_HEADER_FIELDS,
  getFixedFormatTableFields,
  MAP_FORMAT,
  RECORD_LENGTH_FIELD_SETTINGS,
  recordLengthValidators
} from './utils.mapFormats';
import {
  DEFAULT_DELIMITER_OPTION,
  DEFAULT_END_OF_RECORD_OPTION,
  HEADER_FIELDS
} from './constants';

export const HeaderComponent = ({
  achDelimitersData,
  endOfRecordsData,
  formState,
  initialData,
  mapFormat,
  onChange,
  sending,
  validate
}) => {
  const classes = useStyles();
  const locale = useContext(LocaleContext);
  const endOfRecordMethodValidators = useMemo(
    () => delimitedDropdownValidation(locale, 'sameEndOfRecordError'),
    [locale]
  );
  const delimiterValidators = useMemo(
    () => delimitedDropdownValidation(locale, 'sameDelimiterError'),
    [locale]
  );

  const recordLengthValidatorsMemoized = useMemo(() => recordLengthValidators(locale), [locale]);

  const handleRecordLengthBlur = useCallback(() => {
    const tableFields = getFixedFormatTableFields(locale, true, true);

    const pickFieldsData = pick(formState, tableFields);

    const arrayOfFieldNames = Object.keys(pickFieldsData)
      .map((key) => (pickFieldsData[key] ? key : null))
      .filter(Boolean);

    return asyncExecuteMultiplyGluFunctions(arrayOfFieldNames, validate);
  }, [formState, locale, validate]);

  return (
    <div className={classes.wrapper}>
      <MapNameInput
        dataQa="map-name"
        name={HEADER_FIELDS.MAP_NAME}
        label={locale['wireImports.mapName']}
        maxLengthIndicatorLimit={40}
        disabled={sending}
        initialData={initialData}
      />
      {mapFormat === MAP_FORMAT.DELIMITED ? (
        <>
          <DelimitedDropdown
            dataQa="delimiter"
            {...(!formState[HEADER_FIELDS.DELIMITER]
              ? { defaultValue: DEFAULT_DELIMITER_OPTION }
              : {})}
            options={achDelimitersData}
            name={HEADER_FIELDS.DELIMITER}
            className={classnames(classes.dropdown, classes.delimiter)}
            labelText={locale['wireImports.delimiter']}
            validators={delimiterValidators}
            onChange={() => validate(HEADER_FIELDS.END_OF_RECORD_METHOD)}
            disabled={sending}
          />
          <DelimitedDropdown
            dataQa="endOfRecord"
            {...(!formState[HEADER_FIELDS.END_OF_RECORD_METHOD]
              ? { defaultValue: DEFAULT_END_OF_RECORD_OPTION }
              : {})}
            options={endOfRecordsData}
            name={HEADER_FIELDS.END_OF_RECORD_METHOD}
            className={classnames(classes.dropdown, classes.endOfRecord)}
            labelText={locale['wireImports.endOfRecord']}
            validators={endOfRecordMethodValidators}
            onChange={() => validate(HEADER_FIELDS.DELIMITER)}
            disabled={sending}
          />
        </>
      ) : (
        <Input
          data-qa="recordLength"
          type="number"
          min="1"
          name={FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH}
          className="form-group"
          labelText={locale['wireImports.recordLength']}
          disabled={sending}
          validators={recordLengthValidatorsMemoized}
          maxLengthIndicatorLimit={RECORD_LENGTH_FIELD_SETTINGS.MAX_LENGTH}
          onBlur={handleRecordLengthBlur}
        />
      )}
      <div className={classes.extraHeaderOptions}>
        <Checkbox
          data-qa="skipFirstHeaderRecords"
          id={HEADER_FIELDS.SKIP_FIRST_RECORD}
          label={locale['wireImports.skipFirstHeaderRecords']}
          checked={formState[HEADER_FIELDS.SKIP_FIRST_RECORD]}
          className={classes.tableCheckbox}
          onChange={(e) => onChange(HEADER_FIELDS.SKIP_FIRST_RECORD, e.target.checked)}
          disabled={sending}
        />
        {mapFormat === MAP_FORMAT.DELIMITED && (
          <Checkbox
            data-qa="stripQuotesAroundFields"
            id={HEADER_FIELDS.STRIP_QUOTES}
            label={locale['wireImports.stripQuotesAroundFields']}
            checked={formState[HEADER_FIELDS.STRIP_QUOTES]}
            className={classes.tableCheckbox}
            onChange={(e) => onChange(HEADER_FIELDS.STRIP_QUOTES, e.target.checked)}
            disabled={sending}
          />
        )}
        <Checkbox
          data-qa="LoadZero"
          id={HEADER_FIELDS.ZERO_AMOUNT_FIELDS}
          label={locale['wireImports.LoadZero']}
          checked={formState[HEADER_FIELDS.ZERO_AMOUNT_FIELDS]}
          className={classes.tableCheckbox}
          onChange={(e) => onChange(HEADER_FIELDS.ZERO_AMOUNT_FIELDS, e.target.checked)}
          disabled={sending}
        />
      </div>
    </div>
  );
};

HeaderComponent.propTypes = {
  formState: PropTypes.shape({}),
  initialData: PropTypes.shape({}),
  mapFormat: PropTypes.string,
  onChange: PropTypes.func,
  validate: PropTypes.func,
  sending: PropTypes.bool,
  achDelimitersData: PropTypes.arrayOf(PropTypes.shape({})),
  endOfRecordsData: PropTypes.arrayOf(PropTypes.shape({}))
};

HeaderComponent.defaultProps = {
  formState: {},
  mapFormat: null,
  onChange() {},
  validate() {},
  initialData: {},
  sending: false,
  endOfRecordsData: [],
  achDelimitersData: []
};

export default HeaderComponent;
